import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from './LanguageContext';
import { t } from '../utils/translations';
import { Truck, Star, DollarSign, Shield } from 'lucide-react';
const Counter = ({
  end,
  duration = 2.5,
  suffix = ''
}: {
  end: number;
  duration?: number;
  suffix?: string;
}) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  useEffect(() => {
    if (isInView) {
      let startTime: number | null = null;
      const animate = (currentTime: number) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
        // easeOutExpo for more dramatic slow down at the end
        const easeOut = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
        setCount(Math.floor(easeOut * end));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, end, duration]);
  return <span ref={ref} data-id="element-597">
      {count}
      {suffix}
    </span>;
};
export function TrustBar() {
  const {
    lang
  } = useLanguage();
  const text = t[lang].trustBar;
  const stats = [{
    icon: <Truck size={24} className="text-mf-yellow" data-id="element-598" />,
    value: <Counter end={500} suffix="+" data-id="element-599" />,
    label: text.moves
  }, {
    icon: <Star size={24} className="text-mf-yellow" data-id="element-600" />,
    value: '4.9★',
    label: text.rating
  }, {
    icon: <DollarSign size={24} className="text-mf-yellow" data-id="element-601" />,
    value: '100%',
    label: text.pricing
  }, {
    icon: <Shield size={24} className="text-mf-yellow" data-id="element-602" />,
    value: '✓',
    label: text.licensed
  }];
  return <section className="bg-mf-black py-12 relative z-20 overflow-hidden" data-id="element-603">
      {/* Subtle animated background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[200px] bg-mf-red/10 blur-[100px] rounded-full pointer-events-none" data-id="element-604"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-605">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8" data-id="element-606">
          {stats.map((stat, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 30,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          y: 0,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1,
          type: 'spring',
          stiffness: 100,
          damping: 15
        }} className="flex flex-col items-center text-center p-6 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 hover:bg-white/10 transition-colors duration-300 group" data-id="element-607">
              <div className="mb-4 bg-white/10 p-4 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300 group-hover:bg-mf-red/20" data-id="element-608">
                {stat.icon}
              </div>
              <div className="text-4xl md:text-5xl font-condensed font-black text-white mb-2 drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]" data-id="element-609">
                {stat.value}
              </div>
              <div className="text-sm font-bold text-gray-400 uppercase tracking-widest" data-id="element-610">
                {stat.label}
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}